import { BrandLockup } from '../../components/BrandLockup.js'
import { BOARD_PAGE_SIZES, canDeleteBoard, canEditBoard, canManageProject, newId, totalItems, type BoardKind, type BoardSummary } from '@blueprint/domain'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AppVersion } from '../../components/AppVersion.js'
import { UserMenu } from '../../app/UserMenu.js'
import { Button } from '../../components/controls.js'
import { getRepository, targetProjectId, useBoardStore } from '../../state/board-store.js'
import { useMindmapStore } from '../../state/mindmap-store.js'
import { useWorkflowStore } from '../../state/workflow-store.js'
import { readMindmapFile } from '../mind-map/mindmap-files.js'
import { FeedbackTab } from '../feedback/FeedbackTab.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './BoardsView.module.css'

const WHEN = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })

function formatUpdated(iso: string): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? 'Unknown' : WHEN.format(at)
}

/**
 * The landing page. Board management lives here rather than in the board's top bar, so
 * the story map keeps its width for the map itself.
 */
export function BoardsView() {
  const boards = useBoardStore((state) => state.boards)
  const page = useBoardStore((state) => state.boardsPage)
  const phase = useBoardStore((state) => state.phase)
  const setBoardNameDialog = useUiStore((state) => state.setBoardNameDialog)
  const setView = useUiStore((state) => state.setView)

  const kind = useBoardStore((state) => state.boardsKind)
  const selectedProject = useBoardStore((state) => state.selectedProject)
  const project = useBoardStore((state) => state.projects.find((entry) => entry.id === state.selectedProject))
  const setProjectDialog = useUiStore((state) => state.setProjectDialog)
  // New items go into the selected project, so only someone who can edit it is offered them.
  const canCreate = project ? canEditBoard(project.role) : false
  const importInput = useRef<HTMLInputElement>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const first = page.offset + 1
  const last = page.offset + boards.length

  const open = (board: BoardSummary) => {
    if (board.kind === 'mindmap') {
      void useMindmapStore.getState().open(board.id)
      setView('mindmap')
      return
    }
    if (board.kind === 'workflow') {
      void useWorkflowStore.getState().open(board.id)
      setView('workflow')
      return
    }
    void useBoardStore.getState().openBoard(board.id)
    setView('board')
  }

  /** One file opens the new mindmap; several are all created and listed here. */
  const importMindmaps = async (files: File[]) => {
    setNotice(null)
    const problems: string[] = []
    const created: string[] = []
    for (const file of files) {
      try {
        const imported = await readMindmapFile(file)
        if (files.length === 1) {
          const id = await useMindmapStore.getState().create(imported.name, imported.root)
          if (id) {
            setView('mindmap')
            return
          }
          problems.push(`${file.name}: ${useMindmapStore.getState().saveError ?? 'could not be created.'}`)
          continue
        }
        const projectId = targetProjectId()
        await getRepository().createMindmap({ id: newId(), name: imported.name, root: imported.root, ...(projectId ? { projectId } : {}) })
        created.push(imported.name)
      } catch (error) {
        problems.push(error instanceof Error ? error.message : `${file.name}: could not be imported.`)
      }
    }
    await useBoardStore.getState().showBoardsPage({ offset: 0 })
    setNotice(
      [created.length > 0 ? `Imported ${plural(created.length, 'mindmap')}.` : '', ...problems].filter(Boolean).join(' '),
    )
  }

  return (
    <div className={styles.page}>
      {/* The same top and bottom bars as the board view: full width, same surface and
          rules, so moving between the two is not a change of application. */}
      <header className={styles.topBar}>
        <div className={styles.topRow}>
          <span className={styles.brand}>
            <img className={styles.logo} src="/logo-mark.png" alt="" width={34} height={18} />
            <BrandLockup />
          </span>
          <span className={styles.brandSpacer} />
          <UserMenu />
        </div>
      </header>

      <div className={styles.scroll}>
      <div className={styles.layout}>
      <ProjectSidebar />
      <div className={styles.inner}>
        <header className={styles.masthead}>
          <div className={styles.headings}>
            <h1 className={styles.title}>Boards</h1>
            <p className={styles.subtitle}>
              {/* The total comes from storage, not from the rows on screen. */}
              {plural(page.total, 'board')}
              {page.total > page.limit ? ` · showing ${first}–${last}` : ''}
            </p>
          </div>
          {canCreate ? (
            <>
              <button type="button" className={styles.newBoard} onClick={() => setBoardNameDialog('create')}>
                New storymap
              </button>
              <button type="button" className={styles.newBoard} onClick={() => setBoardNameDialog('create-mindmap')}>
                New mindmap
              </button>
              <button type="button" className={styles.newBoard} onClick={() => setBoardNameDialog('create-workflow')}>
                New workflow
              </button>
              <Button onClick={() => importInput.current?.click()} title="Import mindmap files (.json) from the mindmap app">
                Import mindmap…
              </Button>
            </>
          ) : null}
          <input
            ref={importInput}
            type="file"
            accept=".json,application/json"
            multiple
            className="visually-hidden"
            tabIndex={-1}
            onChange={(event) => {
              const files = [...(event.target.files ?? [])]
              event.target.value = ''
              if (files.length > 0) void importMindmaps(files)
            }}
          />
        </header>

        <div className={styles.projectBar}>
          <h2 className={styles.projectName} data-testid="project-heading">
            {project ? project.name : selectedProject === 'shared' ? 'Shared with me' : ''}
          </h2>
          {project ? (
            <>
              <span className={styles.subtitle}>
                {project.role === 'owner' ? 'Owner' : project.role === 'editor' ? 'Editor' : 'Viewer'} · {plural(project.memberCount, 'member')}
              </span>
              <Button onClick={() => setProjectDialog({ kind: 'members', projectId: project.id })}>Members</Button>
            </>
          ) : null}
          {project && canManageProject(project.role) ? (
            <>
              <Button onClick={() => setBoardNameDialog('rename-project')}>Rename</Button>
              <Button variant="danger" onClick={() => setProjectDialog({ kind: 'delete', projectId: project.id })}>
                Delete project
              </Button>
            </>
          ) : null}
        </div>

        <div className={styles.kindFilter} role="group" aria-label="Show">
          {KIND_FILTERS.map((option) => (
            <button
              key={option.label}
              type="button"
              className={styles.kindOption}
              aria-pressed={kind === option.kind}
              onClick={() => void useBoardStore.getState().showBoardsPage({ kind: option.kind, offset: 0 })}
            >
              {option.label}
            </button>
          ))}
        </div>

        {notice ? (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        ) : null}

        {boards.length === 0 ? (
          <div className={styles.list}>
            <p className={styles.empty}>
              {phase === 'loading'
                ? 'Loading boards…'
                : selectedProject === 'shared'
                  ? 'Nothing has been shared with you individually.'
                  : !canCreate
                    ? 'This project is empty.'
                : kind === 'mindmap'
                  ? 'No mindmaps yet. Create or import one to get started.'
                  : kind === 'workflow'
                    ? 'No workflows yet. Create one to get started.'
                  : kind === 'storymap'
                    ? 'No storymaps yet. Create one to get started.'
                    : 'No boards yet. Create one to get started.'}
            </p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Board</th>
                <th scope="col">Type</th>
                <th scope="col">Owner</th>
                <th scope="col">Access</th>
                <th scope="col" className={styles.numeric}>
                  Swimlanes
                </th>
                <th scope="col" className={styles.numeric} title="Cards on a storymap; nodes on a mindmap or workflow">
                  Cards / nodes
                </th>
                <th scope="col">Updated</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {groupLinked(boards).map(({ board, linked }) => (
                <BoardRow key={board.id} board={board} linked={linked} onOpen={open} />
              ))}
            </tbody>
          </table>
        )}

        <Pager />
      </div>
      </div>
      </div>

      <footer className={styles.footerBar}>
        <span className={styles.copyright}>
          <a className={styles.link} href="https://teamcentric.io" target="_blank" rel="noreferrer noopener">
            Teamcentric
          </a>{' '}
          &copy; 2026
        </span>
        <AppVersion className={styles.copyright} />
        <span className={styles.spacer} />
        <FeedbackTab />
      </footer>
    </div>
  )
}

/**
 * Folds each storymap into the row of the mindmap it was generated from, when both are on
 * this page. A storymap whose mindmap is on another page, or filtered out, keeps its own row.
 */
function groupLinked(boards: BoardSummary[]): { board: BoardSummary; linked: BoardSummary[] }[] {
  const mindmapIds = new Set(boards.filter((board) => board.kind === 'mindmap').map((board) => board.id))
  const isFolded = (board: BoardSummary) => Boolean(board.sourceMindmap && mindmapIds.has(board.sourceMindmap.id))
  return boards
    .filter((board) => !isFolded(board))
    .map((board) => ({ board, linked: boards.filter((other) => isFolded(other) && other.sourceMindmap?.id === board.id) }))
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

/** The projects down the left, then the items shared one by one. */
function ProjectSidebar() {
  const projects = useBoardStore((state) => state.projects)
  const selected = useBoardStore((state) => state.selectedProject)
  const setBoardNameDialog = useUiStore((state) => state.setBoardNameDialog)
  const select = (selection: string) => void useBoardStore.getState().selectProject(selection)
  return (
    <nav className={styles.sidebar} aria-label="Projects">
      <p className={styles.sidebarLabel}>Projects</p>
      {projects.map((project) => (
        <button
          key={project.id}
          type="button"
          className={styles.sidebarItem}
          aria-current={selected === project.id ? 'page' : undefined}
          data-testid="project-item"
          data-project-name={project.name}
          onClick={() => select(project.id)}
        >
          <span className={styles.sidebarName}>{project.name}</span>
          <span className={styles.sidebarCount} title="Items in this project">
            {totalItems(project)}
          </span>
        </button>
      ))}
      <button type="button" className={styles.sidebarNew} onClick={() => setBoardNameDialog('create-project')}>
        + New project
      </button>
      <hr className={styles.sidebarRule} />
      <button
        type="button"
        className={styles.sidebarItem}
        aria-current={selected === 'shared' ? 'page' : undefined}
        onClick={() => select('shared')}
      >
        <span className={styles.sidebarName}>Shared with me</span>
      </button>
    </nav>
  )
}

const KIND_FILTERS: { label: string; kind: BoardKind | null }[] = [
  { label: 'All', kind: null },
  { label: 'Storymaps', kind: 'storymap' },
  { label: 'Mindmaps', kind: 'mindmap' },
  { label: 'Workflows', kind: 'workflow' },
]

function KindBadge({ kind }: { kind: BoardKind }) {
  return (
    <span
      className={`${styles.kindBadge} ${kind === 'mindmap' ? styles.kindMindmap : kind === 'workflow' ? styles.kindWorkflow : styles.kindStorymap}`}
      data-testid="board-row-kind"
    >
      {kind === 'workflow' ? (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <rect x="1" y="6" width="4" height="4" rx="0.8" />
          <path d="M11 3.5l3 3-3 3-3-3z" />
          <rect x="10" y="12" width="4" height="3" rx="0.6" />
          <path d="M5 8h3M11 9.5V12" />
        </svg>
      ) : kind === 'mindmap' ? (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="4" cy="8" r="2" />
          <circle cx="12.5" cy="3.5" r="1.8" />
          <circle cx="12.5" cy="12.5" r="1.8" />
          <path d="M6 8c2 0 3-4.5 4.7-4.5M6 8c2 0 3 4.5 4.7 4.5" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <rect x="1.5" y="2" width="13" height="3" rx="0.8" />
          <rect x="1.5" y="7" width="3.5" height="3" rx="0.6" />
          <rect x="6.25" y="7" width="3.5" height="3" rx="0.6" />
          <rect x="11" y="7" width="3.5" height="3" rx="0.6" />
          <rect x="1.5" y="11.5" width="3.5" height="3" rx="0.6" />
          <rect x="6.25" y="11.5" width="3.5" height="3" rx="0.6" />
        </svg>
      )}
      {kind === 'mindmap' ? 'Mindmap' : kind === 'workflow' ? 'Workflow' : 'Storymap'}
    </span>
  )
}

/**
 * One row per board, except that a mindmap and the storymaps generated from it share one:
 * each cell lists the mindmap first, then its storymaps, so each keeps its own actions.
 */
function BoardRow({ board, linked, onOpen }: { board: BoardSummary; linked: BoardSummary[]; onOpen: (board: BoardSummary) => void }) {
  const activeBoardId = useBoardStore((state) => state.activeBoardId)
  const entries = [board, ...linked]
  const each = (render: (entry: BoardSummary) => ReactNode) =>
    entries.map((entry) => (
      <div key={entry.id} className={styles.entry}>
        {render(entry)}
      </div>
    ))

  return (
    <tr
      className={`${styles.row} ${board.kind === 'mindmap' ? styles.rowMindmap : ''}`}
      data-kind={board.kind}
      data-linked={linked.length > 0 ? 'true' : undefined}
    >
      <td>
        {each((entry) => (
          <button
            type="button"
            className={styles.open}
            data-testid="board-row-open"
            data-board-name={entry.name}
            onClick={() => onOpen(entry)}
          >
            <span className={styles.name}>{entry.name}</span>
            {/* Within a shared row the mindmap is the line above; elsewhere it is named. */}
            {entry.sourceMindmap && entry === board ? (
              <span className={styles.source} data-testid="board-row-source">
                from {entry.sourceMindmap.name}
              </span>
            ) : null}
            {entry.sourceWorkflow ? (
              <span className={styles.source} data-testid="board-row-source">
                from {entry.sourceWorkflow.name}
              </span>
            ) : null}
            {entry.kind === 'storymap' && entry.id === activeBoardId ? (
              <span className={styles.active} data-testid="board-row-active">
                Open
              </span>
            ) : null}
          </button>
        ))}
      </td>
      <td>{each((entry) => <KindBadge kind={entry.kind} />)}</td>
      <td className={styles.muted}>{each((entry) => entry.ownerName)}</td>
      <td>
        {each((entry) =>
          entry.access === 'owner' ? (
            <span className={styles.muted}>Owner</span>
          ) : (
            <span className={styles.shared} data-testid="board-row-access">
              {entry.access === 'editor' ? 'Shared: can edit' : 'Shared: view only'}
            </span>
          ),
        )}
      </td>
      {/* The size of the map, so a board can be told apart without opening it. */}
      <td className={styles.numeric} data-testid="board-row-counts">
        {each((entry) => (entry.kind !== 'storymap' ? <span className={styles.muted}>—</span> : entry.swimlaneCount))}
      </td>
      <td className={styles.numeric}>
        {each((entry) => <span title={entry.kind !== 'storymap' ? 'Nodes' : 'Cards'}>{entry.cardCount}</span>)}
      </td>
      <td className={styles.muted}>{each((entry) => formatUpdated(entry.updatedAt))}</td>
      <td>{each((entry) => <RowActions board={entry} />)}</td>
    </tr>
  )
}

function RowActions({ board }: { board: BoardSummary }) {
  const requestConfirm = useUiStore((state) => state.requestConfirm)
  const setSharingBoardId = useUiStore((state) => state.setSharingBoardId)
  // The same rule the API enforces: the owner, or an editor of the project it is in.
  const projectRole = useBoardStore((state) => state.projects.find((project) => project.id === board.projectId)?.role ?? null)
  const canDelete = canDeleteBoard(board.access, projectRole)
  const [menuOpen, setMenuOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && wrapRef.current?.contains(event.target)) return
      setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const move = () => {
    setMenuOpen(false)
    useUiStore.getState().setProjectDialog({ kind: 'move', boardId: board.id })
  }

  const remove = () => {
    setMenuOpen(false)
    const noun = board.kind === 'storymap' ? 'board' : board.kind
    requestConfirm({
      title: `Delete ${noun}`,
      message: `"${board.name}" will be deleted permanently.`,
      confirmLabel: `Delete ${noun}`,
      undoable: false,
      onConfirm: () => void useBoardStore.getState().deleteBoard(board.id),
    })
  }

  return (
    <div className={styles.rowActions}>
      {/* Only the owner decides who else sees a board. */}
      {board.access === 'owner' ? (
        <button
          type="button"
          className={styles.shareButton}
          onClick={() => setSharingBoardId(board.id)}
        >
          Share
        </button>
      ) : null}

      {/* No ⋮ when the menu would be empty, as for a viewer. */}
      {canEditBoard(board.access) || canDelete ? (
      <div className={styles.menuWrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.menuButton}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Actions for ${board.name}`}
        onClick={() => setMenuOpen((value) => !value)}
      >
        &#8942;
      </button>
        {menuOpen ? (
          <div className={styles.menu} role="menu">
            {canEditBoard(board.access) ? (
              <button type="button" role="menuitem" className={styles.item} onClick={move}>
                Move to project…
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                role="menuitem"
                className={`${styles.item} ${styles.itemDanger}`}
                onClick={remove}
              >
                Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  )
}

/**
 * Moves the window the server returns. Nothing is sliced here — each step asks storage
 * for a different page, so a tenant with thousands of boards costs the same as one with
 * three.
 */
function Pager() {
  const page = useBoardStore((state) => state.boardsPage)
  const showBoardsPage = useBoardStore((state) => state.showBoardsPage)
  const pageCount = Math.max(1, Math.ceil(page.total / page.limit))
  const current = Math.floor(page.offset / page.limit) + 1

  return (
    <div className={styles.pager} data-testid="board-pager">
      <label className={styles.pageSize}>
        <span className={styles.pageSizeLabel}>Rows</span>
        <select
          aria-label="Boards per page"
          value={page.limit}
          onChange={(event) => {
            // A different page size starts at the top: keeping the offset would land the
            // reader somewhere arbitrary in the list.
            void showBoardsPage({ limit: Number(event.target.value), offset: 0 })
          }}
        >
          {BOARD_PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <span className={styles.spacer} />

      <span className={styles.pageStatus} data-testid="board-page-status">
        Page {current} of {pageCount}
      </span>
      <Button
        disabled={page.offset === 0}
        onClick={() => void showBoardsPage({ offset: Math.max(0, page.offset - page.limit) })}
      >
        Previous
      </Button>
      <Button
        disabled={current >= pageCount}
        onClick={() => void showBoardsPage({ offset: page.offset + page.limit })}
      >
        Next
      </Button>
    </div>
  )
}
